import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Play, Pause, Download, Music, ExternalLink } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { ru } from "date-fns/locale";
import type { TelegramUser } from "@/hooks/useAdminData";

const statusLabels: Record<string, string> = {
  pending: "Ожидает",
  generating_text: "🥣 Текст",
  text_ready: "📝 Текст готов",
  generating_music: "🍳 Музыка",
  completed: "🎂 Готово",
  failed: "❌ Ошибка",
};

interface UserGenerationsDialogProps {
  user: TelegramUser | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function AudioPlayer({ label, url }: { label: string; url: string }) {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const toggle = () => {
    if (!audioRef.current) {
      audioRef.current = new Audio(url);
      audioRef.current.onended = () => setPlaying(false);
    }
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      audioRef.current.play();
      setPlaying(true);
    }
  };

  return (
    <div className="flex items-center gap-1">
      <Button variant="outline" size="sm" className="h-7 gap-1 text-xs" onClick={toggle}>
        {playing ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
        {label}
      </Button>
      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" asChild>
        <a href={url} download target="_blank" rel="noopener noreferrer">
          <Download className="w-3 h-3" />
        </a>
      </Button>
    </div>
  );
}

export function UserGenerationsDialog({ user, open, onOpenChange }: UserGenerationsDialogProps) {
  const { data: generations, isLoading } = useQuery({
    queryKey: ['user-generations', user?.id],
    enabled: !!user?.id && open,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('generations')
        .select('*, events(*), genres(*), vocals(*)')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const userName = user?.username ? `@${user.username}` : user?.first_name || 'Пользователь';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Music className="w-5 h-5 text-primary" />
            Генерации {userName}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-24 w-full" />)}
          </div>
        ) : !generations?.length ? (
          <p className="text-muted-foreground text-center py-8">Нет генераций</p>
        ) : (
          <div className="space-y-4">
            {generations.map((gen) => (
              <div key={gen.id} className="p-4 rounded-lg bg-muted/50 border border-border/50 space-y-3">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">
                      {format(new Date(gen.created_at), "d MMM yyyy, HH:mm", { locale: ru })}
                    </p>
                    <div className="flex gap-1.5 mt-1">
                      {gen.events && <Badge variant="outline" className="text-xs">{gen.events.emoji} {gen.events.name}</Badge>}
                      {gen.genres && <Badge variant="outline" className="text-xs">{gen.genres.emoji} {gen.genres.name}</Badge>}
                      {gen.vocals && <Badge variant="outline" className="text-xs">{gen.vocals.emoji} {gen.vocals.name}</Badge>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {gen.is_full_purchased && <Badge className="bg-green-500/10 text-green-500 text-xs">Куплен</Badge>}
                    <Badge variant="secondary" className="text-xs">{statusLabels[gen.status] || gen.status}</Badge>
                  </div>
                </div>

                <p className="text-sm text-muted-foreground line-clamp-2">{gen.story}</p>

                {gen.status === 'completed' && (
                  <div className="flex flex-wrap gap-2">
                    {gen.demo_url && <AudioPlayer label="Демо 1" url={gen.demo_url} />}
                    {gen.demo_url_2 && <AudioPlayer label="Демо 2" url={gen.demo_url_2} />}
                    {gen.full_music_url && <AudioPlayer label="Полная 1" url={gen.full_music_url} />}
                    {gen.full_music_url_2 && <AudioPlayer label="Полная 2" url={gen.full_music_url_2} />}
                    {gen.music_url && (
                      <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs" asChild>
                        <a href={gen.music_url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="w-3 h-3" /> Suno
                        </a>
                      </Button>
                    )}
                  </div>
                )}

                {gen.error_message && (
                  <p className="text-xs text-destructive">{gen.error_message}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}